'use client';

import React from 'react';
import { Scale, FileText, Landmark, ScrollText } from 'lucide-react';
import { useCitationContext } from '@/lib/contexts/CitationContext';
import { CitationMap, SourceWithCitation } from '@/lib/utils/citations';
import { CITATIONS_CONFIG } from '@/lib/config/citations.config';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/common/overlay/tooltip/tooltip';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the appropriate Lucide icon for a source type. */
export function getSourceIcon(type: string) {
  switch (type) {
    case 'LAW':
    case 'MODIFIED_LAW':
      return Scale;
    case 'CASELAW':
      return Landmark;
    case 'FORM':
      return ScrollText;
    case 'INFO':
    default:
      return FileText;
  }
}

/** Returns a human-readable type label for the tooltip. */
export function getTypeLabel(type: string): string {
  switch (type) {
    case 'LAW':
      return 'Legislation';
    case 'MODIFIED_LAW':
      return 'Modified Legislation';
    case 'CASELAW':
      return 'Case Law';
    case 'FORM':
      return 'Legal Form';
    case 'INFO':
    default:
      return 'Document';
  }
}

/** Returns the best display title for a source. */
export function getSourceDisplayTitle(source: SourceWithCitation): string {
  if (source.type === 'CASELAW') {
    return source.reference || source.title || source.nomsDesPartis || 'Case Law';
  }
  if (source.type === 'FORM') {
    return source.title || source.filename || 'Form';
  }
  return (
    source.pageTitle ||
    source.title ||
    source.pageSource ||
    source.url ||
    `Source ${source.citationIndex}`
  );
}

/** Returns the page source domain label (pageSource field or hostname extracted from URL). */
export function getPageSourceLabel(source: SourceWithCitation): string | null {
  if (source.pageSource) return source.pageSource;
  const url = source.pageUrl || source.url || source.docUrl;
  if (url) {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CitationPillProps {
  /** The 1-based citation number */
  citationNumber: number;
  /** Explicit citationMap (used when context is unavailable, e.g. inside observer HOC) */
  citationMap?: CitationMap;
}

/**
 * Inline citation pill — shows source type icon + full title.
 * Click scrolls to and highlights the source in the panel.
 */
export const CitationPill: React.FC<CitationPillProps> = ({
  citationNumber,
  citationMap: explicitMap,
}) => {
  const ctx = useCitationContext();
  const map = explicitMap || ctx?.citationMap;

  if (!map || !map[citationNumber]) {
    return null;
  }

  const source = map[citationNumber];
  const Icon = getSourceIcon(source.type);
  const displayTitle = getSourceDisplayTitle(source);
  const typeLabel = getTypeLabel(source.type);
  const pageSource = getPageSourceLabel(source);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ctx?.scrollToSource(citationNumber);
  };

  const pillButton = (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`Citation ${citationNumber}: ${displayTitle}`}
      className="citation-pill inline-flex items-center gap-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[9px] font-medium h-[16px] px-1 cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 active:bg-purple-300 dark:active:bg-purple-900/70 align-sub mx-[1px] transition-colors leading-none select-none max-w-[150px] sm:max-w-[200px]"
    >
      <Icon className="h-2.5 w-2.5 flex-shrink-0" />
      <span className="truncate">{displayTitle}</span>
    </button>
  );

  return (
    <TooltipProvider>
      <Tooltip delayDuration={CITATIONS_CONFIG.BADGE.TOOLTIP_DELAY_MS}>
        <TooltipTrigger asChild>{pillButton}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px]">
          <div className="text-xs space-y-0.5">
            <div className="font-semibold text-popover-foreground">{typeLabel}</div>
            {pageSource && (
              <div className="text-muted-foreground">{pageSource}</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

CitationPill.displayName = 'CitationPill';
